import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { requestApi } from 'utils/request';
import Layout from 'layout';
import {
  DATA_INTERLOCK_NOTICE_MESSAGE,
  DATA_PROVIDER_NOTICE_MESSAGE,
  MALL_LINKAGE_STATUS_LIST,
  DATA_PROVIDER_LINKAGE_STATUS_LIST,
  TAB_VALUES,
} from './constants';
import ROUTE_PATH from 'routes/config';
import { DataLinkageManageWrapper } from './style';
import ListFilter from 'components/ListFilter';
import CustomTab from 'components/CustomTab';
import DataList from './DataList';
import { filterHasValueArgs } from 'tools/WebTool';
import NoticeSection from './NoticeSection';
import { useUI } from '../../../contexts/UIContext';

const DataLinkageManage = () => {
  const navigate = useNavigate();
  const [searchParams, _] = useSearchParams();
  const { setContextLoading, setModalData } = useUI();
  const tabValue = searchParams.get('tab') || TAB_VALUES.MALL_INTERLOCK;

  // 연동 리스트 통합 데이터
  const [listTotalData, setListTotalData] = useState({
    isFilteredSearchYn: '',
    interlockedMallCnt: 0,
    interlockedMallList: [],
  });
  // 필수몰 연동 안내 데이터
  const [essentialMallData, setEssentialMallData] = useState({});
  // 필터 데이터
  const [filterData, setFilterData] = useState(null);
  const [mallSelectOption, setMallSelectOption] = useState([]);
  // 상단 공지 배너
  const [noticeBanner, setNoticeBanner] = useState({});

  const tabDataMap = [
    {
      value: TAB_VALUES.MALL_INTERLOCK,
      name: '판매몰 개별 연동',
    },
    {
      value: TAB_VALUES.DATA_PROVIDER,
      name: '데이터 제공사 연동',
    },
  ];
  const filterOptions =
    tabValue === TAB_VALUES.MALL_INTERLOCK
      ? [
          {
            name: 'mallSeq',
            optionList: mallSelectOption,
            placeholder: '판매몰 전체',
            width: '90px',
          },
          {
            name: 'mallInterlockStatusCd',
            optionList: MALL_LINKAGE_STATUS_LIST,
            placeholder: '연동상태 전체',
            width: '100px',
          },
        ]
      : [
          {
            name: 'providerInterlockStatusCd',
            optionList: DATA_PROVIDER_LINKAGE_STATUS_LIST,
            placeholder: '연동상태 전체',
            width: '100px',
          },
        ];

  const {
    dataList = [],
    dataCount = 0,
    isFilteredSearchYn = 'N',
  } = listTotalData;

  // 데이터 연동하기 버튼 클릭
  const onClickLinkData = () => {
    if (tabValue === TAB_VALUES.MALL_INTERLOCK) {
      navigate(ROUTE_PATH.mypage.dataLinkageDataInterlockRequest);
    } else if (tabValue === TAB_VALUES.DATA_PROVIDER) {
      navigate(ROUTE_PATH.mypage.dataLinkageDataProviderRequest);
    }
  };

  // 연동상태 최신화 버튼 클릭
  const onClickRefreshBtn = () => {
    setContextLoading(true);
    (async () => {
      tabValue === TAB_VALUES.MALL_INTERLOCK
        ? await Promise.all([
            fetchInterlockedMallList(),
            fetchEssentialMallList(),
          ])
        : await fetchDataProviderList();
    })();
  };

  // 연동 데이터 삭제 후처리: 최신화
  const afterDelete = async () => {
    setFilterData({});
    if (tabValue === TAB_VALUES.MALL_INTERLOCK) {
      await fetchEssentialMallList();
      await fetchMallFilter();
    }
  };

  // 데이터 제공 동의를 위한 필수 연동몰 데이터 조회 api
  const fetchEssentialMallList = async () => {
    try {
      const res = await requestApi({
        url: '/v1/au/data-provider-mall/essential-mall-list',
        method: 'GET',
      });

      if (res.success) {
        setEssentialMallData(res.data.result || {});
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 연동 판매몰 리스트 조회 api
  const fetchInterlockedMallList = async () => {
    try {
      const res = await requestApi({
        url: '/v1/au/data-provider-mall/interlocked-mall-list',
        method: 'GET',
        params: { ...filterHasValueArgs(filterData || {}) },
      });

      setListTotalData({
        ...res.data.result,
        dataCount: res.data.result.interlockedMallCnt,
        dataList: res.data.result.interlockedMallList,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setContextLoading(false);
    }
  };

  // 연동 데이터 제공사 리스트 조회 api
  const fetchDataProviderList = async () => {
    try {
      const res = await requestApi({
        url: `/v1/au/data-provider/interlocked-data-provider-list`,
        method: 'GET',
        params: { ...filterHasValueArgs(filterData || {}) },
      });
      setListTotalData({
        ...res.data.result,
        dataCount: res.data.result.dataProviderCnt,
        dataList: res.data.result.dataProvider,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setContextLoading(false);
    }
  };

  // 판매몰 개별 연동 배너 공지 조회 api
  const fetchNoticeBanner = async () => {
    try {
      const res = await requestApi({
        url: '/v1/au/data-provider-mall/notice/banner',
        method: 'GET',
      });
      setNoticeBanner(res.data.result);
    } catch (error) {
      console.error(error);
    }
  };

  // 판매몰 리스트 조회 api
  const fetchMallFilter = async (dataProviderSeq = 0) => {
    try {
      const res = await requestApi({
        method: 'GET',
        url: `/v1/au/common/business/data-provider/${dataProviderSeq}/mall/list`,
      });
      setMallSelectOption(
        res.data.result.map(item => ({
          label: item.mallName,
          value: item.mallSeq,
        }))
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (filterData) {
      (async () => {
        tabValue === TAB_VALUES.MALL_INTERLOCK
          ? await fetchInterlockedMallList()
          : await fetchDataProviderList();
      })();
    }
  }, [filterData]);

  useEffect(() => {
    // 필터 선택 초기화
    setFilterData(null);

    if (tabValue === TAB_VALUES.MALL_INTERLOCK) {
      (async () => {
        await Promise.all([
          !mallSelectOption.length > 0 && fetchMallFilter(),
          fetchInterlockedMallList(),
          fetchEssentialMallList(),
          fetchNoticeBanner(),
        ]);
      })();
    }
    if (tabValue === TAB_VALUES.DATA_PROVIDER) {
      (async () => await fetchDataProviderList())();
    }
  }, [tabValue]);

  return (
    <Layout pageTitle="데이터 연동 관리" isShowBannerPage={false}>
      <DataLinkageManageWrapper>
        {/* 로컬/dev 환경에서만 탭 노출 */}
        {(process.env.REACT_APP_ENV === 'local' ||
          process.env.REACT_APP_ENV === 'develop') && (
          <CustomTab tabValue={tabValue} tabDataMap={tabDataMap} />
        )}
        {!!noticeBanner.noticeSubject &&
          tabValue === TAB_VALUES.MALL_INTERLOCK && (
            <button
              type="button"
              className="noticeBanner"
              onClick={() => {
                navigate(`/service-center/notice/${noticeBanner.noticeSeq}`);
              }}
            >
              <span>{noticeBanner.noticeSubject}</span>
              <FeatherIcon icon="chevron-right" size={20} />
            </button>
          )}
        <div className="paddingWrapper">
          {essentialMallData.essentialMallList?.length > 0 &&
            tabValue === TAB_VALUES.MALL_INTERLOCK && (
              <NoticeSection essentialMallData={essentialMallData} />
            )}
          <button type="button" onClick={onClickLinkData}>
            데이터 연동하기
          </button>
          {dataList.length === 0 && isFilteredSearchYn === 'N' ? (
            tabValue === TAB_VALUES.MALL_INTERLOCK ? (
              DATA_INTERLOCK_NOTICE_MESSAGE
            ) : (
              DATA_PROVIDER_NOTICE_MESSAGE
            )
          ) : (
            <section className="listSection">
              <ListFilter
                totalCount={dataCount}
                setFilterData={setFilterData}
                filterData={filterData}
                selectDataList={filterOptions}
              />
              {!(dataList.length === 0 && isFilteredSearchYn === 'Y') && (
                <button
                  className="refreshBtn"
                  type="button"
                  onClick={onClickRefreshBtn}
                >
                  <span>연동상태 최신화</span>
                </button>
              )}
              {dataList.length === 0 && isFilteredSearchYn === 'Y' ? (
                <p>검색결과가 없습니다.</p>
              ) : (
                <DataList
                  dataList={dataList}
                  setDataList={newList => {
                    setListTotalData({
                      ...listTotalData,
                      dataList: newList,
                    });
                  }}
                  afterDelete={afterDelete}
                  setModalData={setModalData}
                  tabValue={tabValue}
                />
              )}
            </section>
          )}
        </div>
      </DataLinkageManageWrapper>
    </Layout>
  );
};

export default DataLinkageManage;
